<script setup lang="ts">
  import { computed } from "vue";
  import { storeToRefs } from "pinia";
  import { useResultsV2Store } from "@stores/survey/resultsV2/resultsV2";
  import { useChartData } from "@/composables/useChartData";
  import { removeHtmlMarkdownAndLineBreak } from "@utils/toolBox";
  import ResultsBarChart from "./Charts/ResultsBarChart.vue";
  import ResultsStackedChart from "./Charts/ResultsStackedChart.vue";
  import ResultsNumericTable from "./Charts/ResultsNumericTable.vue";
  import { LoadingState } from "@/components/episto-ui/loading-state";

  const props = defineProps<{ cardId: number }>();

  const store = useResultsV2Store();
  const { chartsData } = storeToRefs(store);

  const card = computed(() => ({ id: -props.cardId, card_type: "question", card_id: props.cardId, options: {} }));
  const {
    title,
    respondents,
    isMatrix,
    isNumeric,
    numericStats,
    populationsBarData,
    stackedData,
    matrixChoiceCodes,
    matrixChoiceLabels,
  } = useChartData(card);

  const hasData = computed(() => Object.keys(chartsData.value ?? {}).length > 0);
  const heading = computed(() => (title.value ? removeHtmlMarkdownAndLineBreak(title.value) : ""));
</script>

<template>
  <div class="flex w-full flex-col gap-3">
    <div
      v-if="heading || respondents"
      class="flex items-start justify-between gap-3">
      <p
        v-if="heading"
        class="text-13 font-semibold text-primary">
        {{ heading }}
      </p>
      <span v-else />
      <span
        v-if="respondents"
        class="shrink-0 rounded-full bg-secondary px-2 py-0.5 text-2xs tabular-nums text-muted-foreground">
        n={{ respondents }}
      </span>
    </div>
    <LoadingState
      v-if="!hasData"
      size="sm"
      class="h-24" />
    <ResultsNumericTable
      v-else-if="isNumeric && numericStats"
      :populations="numericStats" />
    <ResultsStackedChart
      v-else-if="isMatrix"
      :data="stackedData"
      :choice-codes="matrixChoiceCodes"
      :choice-labels="matrixChoiceLabels"
      :horizontal="true" />
    <ResultsBarChart
      v-else-if="populationsBarData?.length"
      :populations="populationsBarData"
      :horizontal="true" />
    <p
      v-else
      class="text-xs text-muted-foreground">
      No answers yet for this question.
    </p>
  </div>
</template>
